import { DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Image, Popconfirm } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteFile } from '../../fetchHooks/deleteFile';
import { envApiUrl } from '../../fetchHooks/envApiUrl';
import { useImages } from '../../fetchHooks/useImages';
import 'video-react/dist/video-react.css';
import type { MydayType } from '../../fetchHooks/useMyday.ts';
import FileUpdateDebounced from '../../pages/MyDayPage/FileUpdateDebounced';
import { useEscapeKey } from '../../pages/MyDayPage/Files';
import { ImageModal } from '../MyImage/ImageModal.tsx';
import { type ClickedImageType, MyImage } from '../MyImage/MyImage';
import styles from './images.module.css';

type ImagesProps = {
  myday: MydayType;
  compact?: boolean;
  isEdit?: boolean;
  isExport?: boolean;
};

type ImageType = {
  id: number;
  name: string;
  mimetype: string;
  size: number;
  description: string;
};

const getImageSrc = (id: number) => {
  const maxWidth = 1920;
  const maxHeight = 1680;
  return `${envApiUrl}/files/custom-thumbnail/${id}?maxWidth=${maxWidth}&maxHeight=${maxHeight}&quality=80&format=jpeg`;
};

const getUncompressedImageSrc = (id: number) => {
  return `${envApiUrl}/files/image/${id}`;
};

export const Images = ({ myday, compact = false, isEdit = false, isExport = false }: ImagesProps) => {
  const { t } = useTranslation();
  const { data: images, refetch, isLoading } = useImages(myday.id);
  const [showModal, setShowModal] = useState(false);
  const [clickedImage, setClickedImage] = useState<ClickedImageType | undefined>();

  const goToNextImage = ({ next }: { next: boolean }) => {
    const newArrayNumber = next ? getNextItem(images, clickedImage?.index) : getPrevItem(images, clickedImage?.index);
    const { description, id, name } = images[newArrayNumber];
    const fullImageSrc = getImageSrc(id);

    const uncompressedImageSrc = getUncompressedImageSrc(id);

    setClickedImage({ name, uncompressedImageSrc, src: fullImageSrc, description, index: newArrayNumber });
  };

  // Only add keyboard listeners when modal is open and not in export mode
  useRightArrowKey(showModal && !isExport ? () => goToNextImage({ next: true }) : undefined);
  useLeftArrowKey(showModal && !isExport ? () => goToNextImage({ next: false }) : undefined);
  useEscapeKey(() => setShowModal(false));

  if (!images?.length || isLoading) return;
  if (isEdit)
    return (
      <div>
        <h3>{t('myday.images')}:</h3>
        <div className={styles.images_grid}>
          {images?.map((image: ImageType) => {
            const { id, description } = image;
            const thumbnailSrc = `${envApiUrl}/files/custom-thumbnail/${id}?maxWidth=${800}&maxHeight=${800}&quality=80&format=jpeg`;

            return (
              <div key={'image' + id}>
                <Card
                  cover={<Image src={thumbnailSrc} className={styles.coverImage} />}
                  actions={[
                    <Popconfirm
                      key={`delete-${id}`}
                      title={t('myday.confirmDelete')}
                      onConfirm={() => deleteFile(id, refetch)}
                      onCancel={() => console.log('Canceled')}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                    >
                      <Button
                        danger
                        shape="round"
                        size={compact ? 'small' : undefined}
                        icon={<DeleteOutlined />}
                        ghost
                        className="delete-button"
                      >
                        {t('common.delete')}
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <FileUpdateDebounced
                    fileId={id}
                    label={t('common.description')}
                    propertyName="description"
                    value={description}
                  />
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    );

  if (isExport)
    return (
      <div style={{ maxWidth: '3840px', width: '100%' }}>
        {images?.map((image: ImageType) => {
          const { id, description, name } = image;
          // Use 4K resolution for export - high quality export
          const maxWidth = 3840;
          const maxHeight = 2160;
          const thumbnailSrc = `${envApiUrl}/files/custom-thumbnail/${id}?maxWidth=${maxWidth}&maxHeight=${maxHeight}&quality=95&format=jpeg`;
          return (
            <div
              style={{
                textAlign: 'center',
              }}
              key={id}
            >
              <img
                src={thumbnailSrc}
                style={{
                  maxWidth: '100%',
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 16,
                }}
                key={id}
                alt={''}
              />
              <p>{name === description ? name : description}</p>
            </div>
          );
        })}
      </div>
    );

  return (
    <div>
      <h3>{t('myday.images')}:</h3>
      <div className={styles.images_grid}>
        {images?.map((image: ImageType, index: number) => {
          const { id, name, description } = image;
          // Use thumbnail for display, full image for modal

          const thumbnailSrc = `${envApiUrl}/files/custom-thumbnail/${id}?maxWidth=${600}&maxHeight=${600}&quality=80&format=jpeg`;
          const fullImageSrc = getImageSrc(id);

          const uncompressedImageSrc = getUncompressedImageSrc(id);

          return (
            <MyImage
              compact={compact}
              name={name}
              src={thumbnailSrc}
              key={id}
              fullImageSrc={fullImageSrc}
              description={image.description}
              onClick={() => {
                setShowModal(true);
                setClickedImage({ name, uncompressedImageSrc, src: fullImageSrc, description, index });
              }}
            />
          );
        })}
        {showModal && (
          <ImageModal
            clickedImage={clickedImage}
            onClose={() => setShowModal(false)}
            onClick={() => goToNextImage({ next: true })}
          />
        )}
      </div>
    </div>
  );
};

const getNextItem = <T,>(arr: T[], n: number | undefined): number => {
  if (typeof n === 'undefined') return 0;
  if (arr.length > n + 1) return n + 1;
  return 0;
};

const getPrevItem = <T,>(arr: T[], n: number | undefined): number => {
  if (typeof n === 'undefined') return 0;
  const retVal = arr.length - 1;
  if (n - 1 >= 0) return n - 1;
  return retVal;
};

type Callback = (() => void) | undefined;

export const useRightArrowKey = (callback?: Callback): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowRight') {
        callback?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback]);
};

export const useLeftArrowKey = (callback?: Callback): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowLeft') {
        callback?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback]);
};
